import React, { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import { APAsyncButton, APCard, APColumn, APExpanded, APImage, APRow, APScrollView, APSizedBox, APText, getIt, handleErrorMessage, showErrorDialog, useCountDown } from "ap-components";
import { RouteComponentProps } from "@gatsbyjs/reach-router";
import SharedPrefs from "../../Services/SharedPrefs";
import MandateModel from "../../Models/Mandate";
import { Heading } from ".";
import MutualFundApiServices from "../../Services/MutualFundServices";

// Inline styles for circular progress components
const progressStyles = {
    root: {
        position: "relative" as const,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center"
    },
    bottom: {
        color: "#e0e7f6"
    },
    top: {
        color: "#142e5a",
        animationDuration: "550ms",
        position: "absolute" as const,
        left: 0,
        top: 0
    },
    circle: {
        strokeLinecap: "round" as const
    }
};

export default function UpiMandateLoadingTimer(props: RouteComponentProps<{ mandateId: string }>) {
    const mandateToken = getIt(SharedPrefs).getItem('mandateTkn');
    const [mandate, setMandate] = useState<MandateModel>();
    const totalTime = 1000 * 30
    const [time, { start }] = useCountDown(totalTime);

    useEffect(() => {
        start()
        getMandate();
    }, [])

    if (!mandateToken) {
        window.location.href = "/"
        showErrorDialog("Unauthorized Access");
        return <></>
    }

    if (!props.mandateId) {
        window.history.back();
        showErrorDialog("Please select correct mandate");
        return <></>
    }

    async function getMandate() {
        if (!mandateToken || !props.mandateId) {
            return
        }

        try {
            const res = await getIt(MutualFundApiServices).getMandate(props.mandateId, mandateToken);
            setMandate(res);
        } catch (error) {
            handleErrorMessage(error);
        }
    }

    var minutes = Math.floor(time / 60000);
    var seconds = Math.floor((time % 60000) / 1000);

    let progress = Math.min(100, Math.max(0, 100 - (100 * time / totalTime)));
    if (time || !mandate) {
        return (
            <APColumn mainAxisSize="max" crossAxisAlignment="center" mainAxisAlignment="center" style={{ padding: "30px" }} >
                <APText style={{ lineHeight: 1.5, color: "#41495b", fontSize: "16px", textAlign: "center" }}>
                    Please wait as the process will take upto 30 seconds. Do not press back or refresh
                </APText>
                <APSizedBox height="40px" />
                <div style={progressStyles.root} >
                    <APText
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            letterSpacing: 2,
                            fontWeight: 500,
                            color: "#070417",
                            fontSize: "40px",
                            transform: "translate(-50%, -50%)"
                        }}
                    >
                        0{minutes}:{seconds.toString().padStart(2, '0')}
                    </APText>
                    <CircularProgress
                        variant="determinate"
                        sx={progressStyles.bottom}
                        size={220}
                        thickness={4}
                        value={100}
                    />
                    <CircularProgress
                        variant="determinate"
                        disableShrink
                        sx={{
                            ...progressStyles.top,
                            '& .MuiCircularProgress-circle': progressStyles.circle
                        }}
                        size={220}
                        thickness={4}
                        value={progress}
                    />
                </div>
                <APSizedBox height="24px" />
                <span style={{ lineHeight: 1.5, color: "#41495b", fontSize: "16px" }}>
                    You are being redirected...
                </span>
            </APColumn>
        );
    }



    // let mandateType = mandate.getMandateType();
    var bankName = mandate.bankName?.split(" ")[0] ?? mandate.getBankName()
    return (
        <APColumn mainAxisSize="max" crossAxisAlignment="center" >
            <APExpanded>
                <APScrollView style={{ width: "100%" }}>
                    <APColumn>
                        <APColumn mainAxisAlignment="center" style={{ padding: "16px" }} mainAxisSize="max">
                            <APCard style={{ borderRadius: "8px", padding: "16px", width: "min(100%, 500px)" }}>
                                <APColumn>
                                    <APImage src={"/img/ic_stamp.svg"} />
                                    <APSizedBox height="24px" />
                                    <APText style={{ fontSize: "16px", lineHeight: 1.5, color: "#232730", textAlign: "center" }}>
                                        AutoPay will be enabled immediately after approval in your {MandateModel.getUpiAppName(mandate.upiId, bankName)}.
                                    </APText>
                                    <UpiWhatsNextWithSteps upiId={mandate.upiId} bankName={bankName} />
                                    <APText style={{ fontSize: "16px", lineHeight: 1.5, color: "#232730" }}>
                                        Thank you!
                                    </APText>
                                </APColumn>
                            </APCard>
                        </APColumn>
                    </APColumn>
                </APScrollView>
            </APExpanded>
        </APColumn>
    );
}


const paymentAppAndSteps = {
    'googlePay': [
        '1. Open the Google Pay App',
        '2. Tap on your profile picture in the top right corner',
        '3. Select “UPI Autopay” from the menu',
        '4. You’ll see the pending mandate request(may appear as “Pending” or “Awaiting Approval”)',
        '5. Tap on “Approve” or “Authorise”',
        '6. Enter your UPI PIN to confirm',
    ],
    'phonePay': [
        "1. Open the PhonePe App",
        "2. Go to the “Alerts” section(bell icon at the bottom - right)",
        "3. Tap on the UPI AutoPay notification(may show as “Mandate pending” or similar)",
        "5. Review the mandate details",
        "6. Tap “Approve” or “Authorise”",
        "6. Enter your UPI PIN to confirm the setup",
        // '1. Open the PhonePe App',
        // '2. Tap on the “My Money” section from the home screen',
        // '3. Scroll down and tap on “Auto Debit” under the Payments section',
        // '4. You’ll see the pending mandate request',
        // '5. Tap on “Approve” or “Authorise”',
        // '6. Enter your UPI PIN to confirm',
    ],
    'paytm': [
        '1. Open the Paytm App',
        '2. Type “Automatic Payments” in the search bar and tap on Auto UPI Payments',
        '3. You’ll see the pending mandate request(may show as “Waiting for approval” or “Pending”)',
        '4. Tap on “Approve” or “Authorise”',
        '5. Enter your UPI PIN to confirm',
    ]
}
export function UpiWhatsNextWithSteps({ upiId, bankName }: { upiId?: string, bankName: string }) {
    const userAgent = navigator.userAgent;
    let steps;
    let paymentAppUrl = 'tez://upi/pay';
    if (upiId) {
        let paymentApp = MandateModel.getUpiAppName(upiId, bankName);
        if (paymentApp.startsWith("Google Pay")) {
            steps = paymentAppAndSteps['googlePay'];
        }
        else if (paymentApp.startsWith("PhonePe")) {
            steps = paymentAppAndSteps["phonePay"]
            paymentAppUrl = "phonepe://upi/pay";
        }
        else if (paymentApp.startsWith("Paytm")) {
            steps = paymentAppAndSteps["paytm"]
            paymentAppUrl = "paytmmp://";
        }
    }
    let desc = `After confirmation, please open your UPI app to complete the AutoPay registration.`;
    let stepComponent;
    if (steps) {
        desc = "After confirmation, please follow the steps below to complete the AutoPay registration"
        stepComponent = <>
            {steps.map(step => <APRow gap="8px" style={{ color: "#41495b", fontSize: "14px", lineHeight: 1.43 }} crossAxisAlignment="start">
                <APExpanded style={{ padding: "2px 0" }}>
                    <span>{step}</span>
                </APExpanded>
            </APRow>)}
        </>
    }
    return (
        <APCard style={{ borderRadius: "8px", padding: "16px" }}>
            <Heading
                text={steps ? "Step-by-Step Process for UPI AutoPay Setup" : ""}
                desc={upiId ? undefined : desc}
            />
            {stepComponent && <>
                <APSizedBox height="8px" />
                {stepComponent}
                <APSizedBox height="16px" />
                {(/Mobi|Android/i.test(userAgent) || /Tablet|iPad/i.test(userAgent)) && <APAsyncButton variant="contained" onClick={async () => {
                    window.location.href = paymentAppUrl;
                }}>Approve Your AutoPay Mandate</APAsyncButton>}
            </>

            }

        </APCard>
    )
}